import React, { useMemo, useState } from 'react'

//UI

//Hooks
import useAL from '@/features/al/useAL'
import useECCN from '@/features/eccn/useECCN'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import useZZZ6 from '@/features/zzz6/useZZZ6'

//Helper
import EditableTable from '@/components/Table/EditableTable'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { showModal } from '@/stores/ui/modal/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import modalCallbackRegistry from '@/utils/modalCallbackRegistry'
import { CostBudgetType } from '@eapproval/offer-shared'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import {
  getQuotationDetailExcelColumnIds,
  getQuotationDetailTableColumns,
  validateImportedData,
} from './quotationTeamplateDetailColumns'
import { Flex } from '@chakra-ui/react'

const VendorQuotationDetailTable = ({ removeQuotationDetail, data, disabled, costBudgetType, template = [] }) => {
  const [vendorQuotationECCNDetailModalDetail, setVendorQuotationECCNDetailModalDetail] = useState({
    show: false,
  })
  const { setValue, getValues } = useFormContext()

  const dispatch = useDispatch()
  const { t } = useTranslation()

  const { useFetchAllSystemVariables, useSystemVariableOptions } = useSystemVariable()
  const { data: hsCodeOptions } = useSystemVariableOptions('hsCode', {
    status: 'active',
    labelKey: (row) => `${row.code}`,
  })

  const { useFetchALs } = useAL()
  const { useFetchECCNs } = useECCN()
  const { useFetchZZZ6s } = useZZZ6()

  const { data: alData } = useFetchALs({
    page: 1,
    pageSize: 100000,
    filters: { status: 'active' },
  })

  const { data: eccnData } = useFetchECCNs({
    page: 1,
    pageSize: 100000,
    filters: { status: 'active' },
  })

  const { data: zzz6Data } = useFetchZZZ6s({
    page: 1,
    pageSize: 100000,
    filters: { status: 'active' },
  })

  const alOptions = useMemo(() => {
    if (!alData?.data) return []
    return alData.data.map((al) => ({
      value: al.code,
      label: al.code,
      country: al.country,
      description: al.description,
    }))
  }, [alData])

  const eccnOptions = useMemo(() => {
    if (!eccnData?.data) return []
    return eccnData.data.map((eccn) => ({
      value: eccn.code,
      label: eccn.code,
      country: eccn.country,
      description: eccn.description,
    }))
  }, [eccnData])

  const categoryOptions = useMemo(() => {
    if (!zzz6Data?.data) return []

    const categories = new Set()

    zzz6Data.data.forEach((zzz6) => {
      const rawCategories = zzz6?.Category

      if (Array.isArray(rawCategories)) {
        rawCategories.forEach((category) => {
          if (typeof category === 'string' && category.trim() !== '') {
            categories.add(category.trim())
          }
        })
        return
      }

      if (typeof rawCategories === 'string' && rawCategories.trim() !== '') {
        categories.add(rawCategories.trim())
      }
    })

    return Array.from(categories)
      .sort((left, right) => left.localeCompare(right))
      .map((category) => ({ value: category, label: category }))
  }, [zzz6Data])

  const disableCategory = [
    CostBudgetType.SUPPLY_ENGINEERING_SERVICE,
    CostBudgetType.SUPPLY_MAINTENANCE_SERVICE,
  ].includes(costBudgetType)

  // prevent from pressing enter key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  })

  const onCellPaste = (pastedText, context = {}) => {
    try {
      const rows = pastedText
        .split('\n')
        .map((row) => row.replace('\r', ''))
        .filter((row) => row !== '')

      if (rows.length === 0) throw new Error("Empty data can't be pasted")

      const parsedRows = rows.map((row) => row.split('\t'))
      const isSingleColumnPaste = parsedRows.every((cells) => cells.length === 1)

      if (isSingleColumnPaste && context?.columnId && Number.isInteger(context?.rowIndex)) {
        parsedRows.forEach((cells, index) => {
          const targetRowIndex = context.rowIndex + index

          if (targetRowIndex >= data.length) {
            return
          }

          updateTable(targetRowIndex, context.columnId, cells[0] ?? '')
        })

        return
      }

      const excelHeader = getQuotationDetailExcelColumnIds(costBudgetType)
      const formattedRows = parsedRows.map((cells) => {
        //Check if the cell length is the same as the table columns

        if (cells.length !== excelHeader.length) {
          throw new Error(t('VENDOR_UPLOAD_PAGE_ERROR_PASTE_DATA_MISMATCHED_COLUMN'))
        }

        let rowObject = {
          ECCNDetail: [
            {
              description: 'VENDOR_UPLOAD_PAGE_QUESTIONNAIRE_BASIC_QUERY_2',
              selected: false,
              productName: '',
            },
            {
              description: 'VENDOR_UPLOAD_PAGE_QUESTIONNAIRE_BASIC_QUERY_3',
              selected: false,
              productName: '',
            },
            {
              description: 'VENDOR_UPLOAD_PAGE_QUESTIONNAIRE_BASIC_QUERY_4',
              selected: false,
              productName: '',
            },
          ],
        }
        for (let i = 0; i < excelHeader.length; i++) {
          const cellValue = cells[i] ?? ''

          if (['quantity', 'unitRate', 'discount', 'totalPrice'].includes(excelHeader[i])) {
            rowObject[excelHeader[i]] = isNaN(parseFloat(cellValue)) ? 0 : parseFloat(cellValue)
          } else if (excelHeader[i] === 'HSCode') {
            //Check if the ECCN is valid
            if (hsCodeOptions.some((opt) => opt.value === cellValue.trim())) {
              rowObject[excelHeader[i]] = cellValue.trim()
            }
          } else {
            rowObject[excelHeader[i]] = cellValue
          }
        }

        return rowObject
      })

      validateImportedData(formattedRows, template)

      setValue('quotationDetails', formattedRows, { shouldDirty: true })
    } catch (err) {
      toast.error(err.message)
    }
  }

  const updateTable = (row, columnId, value) => {
    if (columnId === 'unitRate' || columnId === 'quantity' || columnId === 'discount') {
      const integerValue = parseFloat(value)
      if (isNaN(integerValue)) {
        setValue(`quotationDetails.${row}.${columnId}`, 0, { shouldDirty: true })
        return
      }
      const unitRate = columnId === 'unitRate' ? integerValue : (getValues(`quotationDetails.${row}.unitRate`) ?? 0)
      const quantity = columnId === 'quantity' ? integerValue : (getValues(`quotationDetails.${row}.quantity`) ?? 0)
      const discount = columnId === 'discount' ? integerValue : (getValues(`quotationDetails.${row}.discount`) ?? 0)

      const totalPrice = (quantity * unitRate * (1 - discount / 100)).toFixed(2)
      setValue(`quotationDetails.${row}.totalPrice`, parseFloat(totalPrice), { shouldDirty: true })
      setValue(`quotationDetails.${row}.${columnId}`, integerValue, { shouldDirty: true })
    } else {
      setValue(`quotationDetails.${row}.${columnId}`, value, { shouldDirty: true })

      let matchingZZZ6
      if (columnId === 'country' || columnId === 'category' || columnId === 'ECCN') {
        const country = columnId === 'country' ? value : getValues(`quotationDetails.${row}.country`)
        const category = columnId === 'category' ? value : getValues(`quotationDetails.${row}.category`)
        const ECCN = columnId === 'ECCN' ? value : getValues(`quotationDetails.${row}.ECCN`)

        if (!country || !category || !ECCN) return

        matchingZZZ6 = zzz6Data?.data?.find((zzz6) => {
          const countryMatches =
            zzz6.country &&
            Array.isArray(zzz6.country) &&
            zzz6.country.some((c) => c.toLowerCase().trim() === country.toLowerCase().trim())

          const categoryMatches =
            zzz6.Category &&
            Array.isArray(zzz6.Category) &&
            zzz6.Category.some((cat) => cat.toLowerCase().trim() === category.toLowerCase().trim())

          const ECCNMatches = zzz6.ECCN && zzz6.ECCN.toLowerCase().trim() === ECCN.toLowerCase().trim()

          return countryMatches && categoryMatches && ECCNMatches
        })
      }

      if (columnId === 'scopeOfWork' && costBudgetType === CostBudgetType.SUPPLY_SITE_WORK) {
        if (value !== 'Supply only') {
          //Find zzz6
          matchingZZZ6 = zzz6Data?.data?.find((zzz6) => {
            return (
              Array.isArray(zzz6.Category) &&
              zzz6.Category.some(
                (cat) => cat.toLowerCase().trim() === 'General Electrical Installation'.toLowerCase().trim()
              )
            )
          })
          setValue(`quotationDetails.${row}.category`, 'General Electrical Installation', { shouldDirty: true })
        } else {
          setValue(`quotationDetails.${row}.category`, '', { shouldDirty: true })
          setValue(`quotationDetails.${row}.SAPMaterialNumber`, '', { shouldDirty: true })
        }
      }

      if (!matchingZZZ6 && ['country', 'category', 'ECCN'].includes(columnId)) {
        setValue(`quotationDetails.${row}.SAPMaterialNumber`, '', {
          shouldDirty: true,
        })
        return
      }

      if (matchingZZZ6?.AL) {
        const matchedALOption = alOptions.find((opt) => opt.value === matchingZZZ6.AL)
        setValue(`quotationDetails.${row}.AL`, matchedALOption?.value ?? matchingZZZ6.AL, { shouldDirty: true })
      }

      if (matchingZZZ6?.ECCN) {
        const matchedECCNOption = eccnOptions.find((opt) => opt.value === matchingZZZ6.ECCN)
        setValue(`quotationDetails.${row}.ECCN`, matchedECCNOption?.value ?? matchingZZZ6.ECCN, {
          shouldDirty: true,
        })
      }

      if (matchingZZZ6?.HScode) {
        const matchedHSCodeOption = hsCodeOptions.find((opt) => opt.value === matchingZZZ6.HScode)
        setValue(`quotationDetails.${row}.HSCode`, matchedHSCodeOption?.value ?? matchingZZZ6.HScode, {
          shouldDirty: true,
        })
      }
      if (matchingZZZ6?.ZZ6) {
        setValue(`quotationDetails.${row}.SAPMaterialNumber`, matchingZZZ6.ZZ6, {
          shouldDirty: true,
        })
      }
    }
  }

  const handleRemoveRow = (index) => {
    removeQuotationDetail(index)
  }

  const handleOpenECCNDetailModel = (ECCNDetail, index) => {
    modalCallbackRegistry.handleUpdateECCNDetail = (data) => {
      setValue(`quotationDetails.${index}.ECCNDetail`, data, { shouldDirty: true })
    }

    dispatch(
      showModal({
        modalType: MODAL_TYPES.ECCN_DETAIL,
        modalProps: {
          ECCNDetail: ECCNDetail,
          canEdit: !disabled,
        },
      })
    )
  }

  const columnDef = getQuotationDetailTableColumns({
    costBudgetType: costBudgetType,
    hsCodeOptions: hsCodeOptions ?? [],
    alOptions: alOptions,
    eccnOptions: eccnOptions,
    categoryOptions: categoryOptions,
    disableCategory: disableCategory,
    getValues: getValues,
    handleRemove: handleRemoveRow,
    onCellPaste,
    updateTable,
    handleOpenECCNDetailModel,
    template: template,
  })

  return (
    <Flex w="100%" overflowX={'auto'} gap="3">
      <EditableTable
        textAlign={'left'}
        columnDef={columnDef}
        data={data}
        disabled={disabled}
        updateData={updateTable}
      />
    </Flex>
  )
}

export default VendorQuotationDetailTable
